import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';
import { cn } from 'lib/utils';
import {
  ConfigureConnectAppsIcon,
  ConfigureCreateAgentIcon,
  ConfigureCreateSkillsIcon,
} from './configure-dashboard-icons';

type ConfigureActionIcon = typeof ConfigureCreateAgentIcon;

type ConfigureAction = {
  id: string;
  titleKey: string;
  titleDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
  Icon: ConfigureActionIcon;
  path: string;
};

const CARD_ICON_SIZE = 32;
const HEADER_ICON_SIZE = 96;

const CONFIGURE_ACTIONS: ConfigureAction[] = [
  {
    id: 'create-agent',
    titleKey: 'CREATE_AGENT',
    titleDefault: 'Create Agent',
    descriptionKey: 'CONFIGURE_CREATE_AGENT_DESCRIPTION',
    descriptionDefault: 'Let Klax create agents for you to assist in your tasks',
    Icon: ConfigureCreateAgentIcon,
    path: '/agents',
  },
  {
    id: 'create-skills',
    titleKey: 'CONFIGURE_CREATE_SKILLS_TITLE',
    titleDefault: 'Create New Skills',
    descriptionKey: 'CONFIGURE_CREATE_SKILLS_DESCRIPTION',
    descriptionDefault: 'Teach Klax your process, team norms, and expertise.',
    Icon: ConfigureCreateSkillsIcon,
    path: '/skills',
  },
  {
    id: 'connect-apps',
    titleKey: 'CONFIGURE_CONNECT_APPS_TITLE',
    titleDefault: 'Connect Your Apps',
    descriptionKey: 'CONFIGURE_CONNECT_APPS_DESCRIPTION',
    descriptionDefault: 'Let Klax read and write to the tools you already use.',
    Icon: ConfigureConnectAppsIcon,
    path: '/knowledge-base?tab=integrations',
  },
];

function ConfigureActionCard({ action }: { action: ConfigureAction }) {
  const { t } = useTranslation();
  const ActionIcon = action.Icon;

  return (
    <Link
      to={action.path}
      className={cn(
        'flex w-full shrink-0 items-center gap-6 rounded-lg border border-solid border-grey-100 bg-white p-6',
        'transition-colors hover:bg-grey-50',
        'dark:border-transparent dark:bg-[#1d1d1d] dark:hover:bg-[#242424]'
      )}
    >
      <div
        className={cn(
          'relative flex size-12 shrink-0 items-center justify-center rounded-[4px] border-[0.5px] border-solid border-[#D1D1D1] bg-grey-50',
          'dark:border-transparent dark:bg-[#171717]'
        )}
      >
        <ActionIcon
          width={CARD_ICON_SIZE}
          height={CARD_ICON_SIZE}
          className="text-[#3D3D3D] dark:text-yellow-100"
          aria-hidden
        />
      </div>
      <div className="flex min-w-0 flex-col items-start gap-1.5">
        <p className="w-full overflow-hidden text-ellipsis text-lg font-semibold leading-[1.5] text-[#3D3D3D] dark:text-true-white">
          {t(action.titleKey, { defaultValue: action.titleDefault })}
        </p>
        <p className="overflow-hidden text-ellipsis text-sm font-normal leading-[1.2] text-[#6D6D6D] dark:text-grey-100">
          {t(action.descriptionKey, { defaultValue: action.descriptionDefault })}
        </p>
      </div>
    </Link>
  );
}

export function ConfigureDashboardPage() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-full w-full flex-1 items-center justify-center py-12 md:py-16">
      <div className="flex w-full max-w-[754px] flex-col items-center gap-[60px]">
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full flex-col items-center gap-4">
            <SlidersHorizontal
              size={HEADER_ICON_SIZE}
              strokeWidth={1.5}
              className="shrink-0 text-[#3D3D3D] dark:text-true-white"
              aria-hidden
            />
            <h1 className="w-full text-center text-[30px] font-semibold leading-[1.2] text-[#3D3D3D] dark:text-true-white">
              {t('ADVANCED_CONFIGURATIONS', { defaultValue: 'Advanced Configurations' })}
            </h1>
          </div>
          <p className="w-full text-center text-lg font-normal leading-[1.6] text-[#4F4F4F] dark:text-grey-100">
            {t('CONFIGURE_KLAX_DESCRIPTION', {
              defaultValue: 'Skills, Connectors, and plugins shape how Klax works with you',
            })}
          </p>
        </div>

        <div className="flex w-full max-w-[560px] flex-col items-start gap-4">
          {CONFIGURE_ACTIONS.map((action) => (
            <ConfigureActionCard key={action.id} action={action} />
          ))}
        </div>
      </div>
    </div>
  );
}
